import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { SessionLock } from "@/components/SessionLock";
import {
  LayoutDashboard, Users, Stethoscope, Building, CalendarCheck, ClipboardList,
  BedDouble, Receipt, BarChart3, Pill, ScanBarcode, Boxes, Truck, Undo2, Wallet, IndianRupee,
  ShieldCheck, Bell, Settings, LogOut, Menu, FileClock, UserCog, Package, Baby,
  Sun, Moon, MonitorSmartphone, Sparkles, ScanLine, FlaskConical, TestTube, Handshake,
  ScrollText, HeartPulse, FolderOpen, MessageSquare, Layers, MapPin, Bike, Microscope,
  FilePlus2, ArrowLeftRight, PenLine, CalendarClock, ScanHeart,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import { Footer } from "@/components/Bits";
import { LogoMark } from "@/components/Brand";

const OWNER_NAV = [
  { to: "/owner", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/owner/businesses", label: "Businesses", icon: Building },
  { to: "/owner/groups", label: "Groups", icon: Handshake },
  { to: "/owner/users", label: "Platform Users", icon: UserCog },
  { to: "/owner/audit", label: "Audit Log", icon: FileClock },
];

const AI_NAV = [
  { to: "/app/ai", label: "AI Copilot", icon: Sparkles },
];

const HOSPITAL_NAV = [
  { to: "/app/hospital", label: "Dashboard", icon: LayoutDashboard, perm: "hospital.dashboard", end: true },
  { to: "/app/hospital/patients", label: "Patients", icon: Users, perm: "hospital.patients.view" },
  { to: "/app/hospital/doctors", label: "Doctors & Departments", icon: Stethoscope, perm: "hospital.dashboard" },
  { to: "/app/hospital/appointments", label: "Appointments", icon: CalendarCheck, perm: "hospital.appointments.manage" },
  { to: "/app/hospital/follow-ups", label: "Follow-ups", icon: CalendarClock, perm: "hospital.followups.manage" },
  { to: "/app/hospital/opd", label: "OPD", icon: ClipboardList, perm: "hospital.opd.manage" },
  { to: "/app/hospital/ipd", label: "IPD & Beds", icon: BedDouble, perm: "hospital.ipd.manage" },
  { to: "/app/hospital/maternity", label: "Maternity", icon: Baby, perm: "hospital.maternity.manage" },
  { to: "/app/hospital/birth", label: "Birth Records", icon: ScrollText, perm: "hospital.birth.view" },
  { to: "/app/hospital/death", label: "Death Records", icon: HeartPulse, perm: "hospital.death.view" },
  { to: "/app/hospital/documents", label: "Documents & Scans", icon: FolderOpen, perm: "hospital.documents.view" },
  { to: "/app/hospital/stock", label: "Internal Stock", icon: Boxes, perm: "hospital.stock.view" },
  { to: "/app/hospital/expenses", label: "Hospital Expenses", icon: Wallet, perm: "hospital.expenses.manage" },
  { to: "/app/hospital/earnings", label: "Doctor Earnings", icon: IndianRupee, perm: "hospital.earnings.view" },
  { to: "/app/hospital/reports", label: "Reports", icon: BarChart3, perm: "hospital.reports.view" },
  { to: "/app/hospital/packages", label: "Special Packages", icon: Package, perm: "hospital.packages.manage" },
];

const PHARMACY_NAV = [
  { to: "/app/pharmacy", label: "Dashboard", icon: LayoutDashboard, perm: "pharmacy.dashboard", end: true },
  { to: "/app/pharmacy/pos", label: "POS Counter", icon: ScanBarcode, perm: "pharmacy.sales.create" },
  { to: "/app/pharmacy/sales", label: "Sales History", icon: Receipt, perm: "pharmacy.sales.view" },
  { to: "/app/pharmacy/b2b", label: "B2B Credit", icon: Handshake, perm: "pharmacy.b2b.view" },
  { to: "/app/pharmacy/inventory", label: "Inventory", icon: Boxes, perm: "pharmacy.inventory.view" },
  { to: "/app/pharmacy/purchases", label: "Purchases", icon: Package, perm: "pharmacy.inventory.view" },
  { to: "/app/pharmacy/bill-scan", label: "AI Bill Scan", icon: ScanLine, perm: "pharmacy.purchase.create" },
  { to: "/app/pharmacy/returns", label: "Returns", icon: Undo2, perm: "pharmacy.sales.view" },
  { to: "/app/pharmacy/expenses", label: "Expenses", icon: Wallet, perm: "pharmacy.expenses.manage" },
  { to: "/app/pharmacy/reports", label: "Reports", icon: BarChart3, perm: "pharmacy.reports.view" },
];

const LAB_NAV = [
  { to: "/app/lab", label: "Dashboard", icon: LayoutDashboard, perm: "lab.dashboard", end: true },
  { to: "/app/lab/orders", label: "Laboratory Orders", icon: ClipboardList, perm: "lab.orders.view" },
  { to: "/app/lab/new-bill", label: "New Bill", icon: FilePlus2, perm: "lab.orders.create" },
  { to: "/app/lab/daily-business", label: "Daily Business", icon: Wallet, perm: "lab.reports.view" },
  { to: "/app/lab/expenses", label: "Expenses", icon: IndianRupee, perm: "lab.reports.view" },
  { to: "/app/lab/due-report", label: "Due Reports", icon: FileClock, perm: "lab.reports.view" },
  { to: "/app/lab/referral-business", label: "Referral Business", icon: ScrollText, perm: "lab.reports.view" },
  { to: "/app/lab/case-wise", label: "Case-wise Report", icon: ClipboardList, perm: "lab.reports.view" },
  { to: "/app/lab/business-analysis", label: "Business Analysis", icon: BarChart3, perm: "lab.reports.view" },
  { to: "/app/lab/data-export", label: "Data Export", icon: FolderOpen, perm: "lab.reports.export" },
  { to: "/app/lab/bills", label: "Bills", icon: Receipt, perm: "lab.orders.view" },
  { to: "/app/lab/outsource", label: "Outsource Cases", icon: ArrowLeftRight, perm: "lab.orders.view" },
  { to: "/app/lab/ct-scan", label: "CT Scan Cases", icon: ScanHeart, perm: "lab.orders.view" },
  { to: "/app/lab/patients", label: "Patients", icon: Users, perm: "lab.patients.view" },
  { to: "/app/lab/transactions", label: "Transactions", icon: ArrowLeftRight, perm: "lab.orders.view" },
  { to: "/app/lab/today", label: "Today's Reports", icon: CalendarClock, perm: "lab.orders.view" },
  { to: "/app/lab/search-reports", label: "Search Reports", icon: Microscope, perm: "lab.orders.view" },
  { to: "/app/lab/tests", label: "Test Database", icon: Microscope, perm: "lab.tests.view" },
  { to: "/app/lab/categories", label: "Test Categories", icon: Layers, perm: "lab.tests.view" },
  { to: "/app/lab/panels", label: "Test Panels", icon: Boxes, perm: "lab.tests.view" },
  { to: "/app/lab/packages", label: "Test Packages", icon: Package, perm: "lab.tests.view" },
  { to: "/app/lab/services", label: "Diagnostic Services", icon: ScanHeart, perm: "lab.tests.view" },
  { to: "/app/lab/diagnostic/today", label: "Imaging — Today's Cases", icon: ScanHeart, perm: "diagnostic.access" },
  { to: "/app/lab/diagnostic/search", label: "Imaging — Search Cases", icon: Microscope, perm: "diagnostic.access" },
  { to: "/app/lab/signatures", label: "Signatures", icon: PenLine, perm: "lab.orders.view" },
  { to: "/app/lab/referrers", label: "Referral Doctors", icon: Stethoscope, perm: "lab.orders.view" },
  { to: "/app/lab/centres", label: "Collection Centres", icon: MapPin, perm: "lab.orders.view" },
  { to: "/app/lab/agents", label: "Sample Agents", icon: Bike, perm: "lab.orders.view" },
  { to: "/app/lab/partners", label: "B2B Partners", icon: Handshake, perm: "lab.orders.view" },
  { to: "/app/lab/reports", label: "Reports", icon: BarChart3, perm: "lab.reports.view" },
];

const ADMIN_NAV = [
  { to: "/app/invoices", label: "Central Invoices", icon: Receipt, perms: ["hospital.billing.manage", "pharmacy.sales.view", "lab.orders.view"] },
  { to: "/app/staff", label: "Staff & Permissions", icon: ShieldCheck, perm: "staff.manage" },
  { to: "/app/staff/shifts", label: "Staff Shifts", icon: CalendarClock, perm: "staff.shifts.view" },
  { to: "/app/payroll", label: "Payroll", icon: Wallet, perm: "payroll.view" },
  { to: "/app/referrals", label: "Referrals", icon: Truck, perm: "referrals.view" },
  { to: "/app/finance", label: "Month-end & GST", icon: BarChart3, perm: "finance.reports.view" },
  { to: "/app/reports/center", label: "Reports Center", icon: BarChart3, perm: "hospital.reports.view" },
  { to: "/app/reports/consolidated", label: "Consolidated Report", icon: BarChart3, perm: "finance.reports.view" },
  { to: "/app/settings/whatsapp?module=hospital", label: "Hospital WhatsApp", icon: MessageSquare, perm: "hospital.whatsapp.manage" },
  { to: "/app/settings/whatsapp?module=lab", label: "Lab WhatsApp", icon: MessageSquare, perm: "lab.whatsapp.manage" },
  { to: "/app/settings/whatsapp?module=pharmacy", label: "Pharmacy WhatsApp", icon: MessageSquare, perm: "pharmacy.whatsapp.manage" },
  { to: "/app/attendance", label: "Staff Attendance", icon: CalendarCheck, perm: "attendance.view" },
  { to: "/app/notifications", label: "Notifications", icon: Bell, perm: "notifications.view" },
  { to: "/app/audit", label: "Audit Log", icon: FileClock, perm: "audit.view" },
  { to: "/app/settings", label: "Settings", icon: Settings, perms: ["settings.manage", "hospital.prescriptions.manage", "lab.templates.manage"] },
];

const NavGroup = ({ title, items, can, onNavigate, scope = "" }) => {  const visible = items.filter((i) => i.perms ? i.perms.some(can) : !i.perm || can(i.perm));
  if (!visible.length) return null;
  return (
    <div className="mb-7">
      {title && <p className="overline text-slate-400 px-3 mb-3">{title}</p>}
      <nav className="space-y-0.5">
        {visible.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate}
            data-testid={`nav-${scope}${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-md px-3 py-2 text-[13px] font-medium ${
                isActive ? "bg-[#0F172A] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              }`}>
            <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.7} />
            <span className="truncate">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

const THEME_OPTIONS = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: MonitorSmartphone },
];

const ThemeToggle = () => {
  const { mode, setMode, dark } = useTheme();
  const [open, setOpen] = useState(false);
  const Current = dark ? Moon : Sun;
  return (
    <div className="relative">
      <button onClick={() => setOpen((o) => !o)} data-testid="theme-toggle-btn"
        aria-label="Change appearance"
        className="grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-slate-600 hover:border-slate-400">
        <Current className="h-4 w-4" strokeWidth={1.8} />
      </button>
      {open && (
        <>
          <button className="fixed inset-0 z-40 cursor-default" aria-hidden="true" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-2 w-44 rounded-lg border border-slate-200 bg-white p-1.5 shadow-xl"
            data-testid="theme-menu">
            <p className="px-3 py-2 overline text-slate-400">Appearance</p>
            {THEME_OPTIONS.map((o) => (
              <button key={o.value} onClick={() => { setMode(o.value); setOpen(false); }}
                data-testid={`theme-option-${o.value}`}
                className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-[13px] font-medium ${
                  mode === o.value ? "bg-slate-100 text-slate-900" : "text-slate-600 hover:bg-slate-50"}`}>
                <o.icon className="h-4 w-4" strokeWidth={1.7} /> {o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default function AppShell({ ownerMode = false }) {  const { user, loading, logout, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!user) return;
    api.get("/notifications").then(({ data }) => setUnread(data.unread || 0)).catch(() => {});
  }, [user, location.pathname]);

  useEffect(() => {
    if (loading) return;
    if (user === false) navigate("/login", { replace: true });
    else if (user && ownerMode && user.role !== "platform_owner") navigate("/app", { replace: true });
    else if (user && !ownerMode && user.role === "platform_owner") navigate("/owner", { replace: true });
  }, [user, loading, ownerMode, navigate]);

  if (loading || !user) {
    return <div className="min-h-screen grid place-items-center app-canvas text-sm text-slate-400">Loading workspace…</div>;
  }

  const modules = user.modules || [];
  const renderSidebar = (prefix = "") => (
    <div className="flex h-full flex-col">
      <div className="px-6 h-16 flex items-center border-b border-slate-200">
        <Link to="/" className="flex items-center gap-2.5">
          <LogoMark className="h-8 w-8" />
          <span className="font-ui font-extrabold tracking-tight text-[13px] leading-tight">
            AJ CLASSIC<span className="text-slate-500"> HEALTHCARE</span>
          </span>
        </Link>
      </div>
      <div className="px-3 py-6 overflow-y-auto flex-1">
        {ownerMode ? (
          <NavGroup title="Platform" scope={prefix} items={OWNER_NAV} can={() => true} onNavigate={() => setOpen(false)} />
        ) : (
          <>
            <NavGroup title="Assistant" scope={prefix} items={AI_NAV} can={() => true} onNavigate={() => setOpen(false)} />
            {modules.includes("hospital") && (
              <NavGroup title="Hospital" scope={`${prefix}hospital-`} items={HOSPITAL_NAV} can={can} onNavigate={() => setOpen(false)} />
            )}
            {modules.includes("pharmacy") && (
              <NavGroup title="Pharmacy" scope={`${prefix}pharmacy-`} items={PHARMACY_NAV} can={can} onNavigate={() => setOpen(false)} />
            )}
            {modules.includes("lab") && (
              <NavGroup title="Laboratory" scope={`${prefix}lab-`} items={LAB_NAV} can={can} onNavigate={() => setOpen(false)} />
            )}
            <NavGroup title="Administration" scope={prefix} items={ADMIN_NAV} can={can} onNavigate={() => setOpen(false)} />
          </>
        )}
      </div>
      <div className="border-t border-slate-200 p-4">
        <div className="rounded-lg bg-slate-50 p-4">
          <p className="text-[13px] font-semibold text-slate-900 truncate" data-testid={`${prefix}sidebar-user-name`}>{user.name}</p>
          <p className="text-xs text-slate-500 truncate">{user.email}</p>
          <p className="mt-2 overline text-slate-400">{String(user.role).replace(/_/g, " ")}</p>
          {user.business_name && (
            <p className="text-xs text-slate-500 mt-2" data-testid={`${prefix}sidebar-business-name`}>
              {user.business_name} · {user.business_code}
            </p>
          )}
        </div>
        <button onClick={async () => { await logout(); navigate("/login"); }} data-testid={`${prefix}logout-btn`}
          className="mt-3 flex w-full items-center gap-2 rounded-md px-3 py-2 text-[13px] text-slate-600 hover:bg-slate-100 hover:text-slate-900">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen app-canvas">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-[268px] bg-white border-r border-slate-200 z-40">
        {renderSidebar()}
      </aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-[280px] max-w-[85vw] bg-white flex flex-col" data-testid="mobile-sidebar">
            {renderSidebar("mobile-")}
          </div>
          <button className="flex-1 bg-slate-900/40" onClick={() => setOpen(false)} aria-label="Close menu" />
        </div>
      )}
      <div className="lg:pl-[268px] flex min-h-screen flex-col">
        <header className="glass-header sticky top-0 z-30 h-16 flex items-center justify-between px-5 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="open-sidebar-btn" aria-label="Open menu">
              <Menu className="h-5 w-5 text-slate-700" />
            </button>
            <div>
              <p className="text-[13px] font-semibold text-slate-900">
                {ownerMode ? "Platform Owner Console" : user.business_name || "Workspace"}
              </p>
              <p className="text-[11px] text-slate-400">
                {ownerMode ? "AJ Classic Healthcare" : `Business ID ${user.business_code || "—"}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            {!ownerMode && can("notifications.view") && (
              <Link to="/app/notifications" data-testid="header-notifications-btn"
                className="relative rounded-full border border-slate-200 bg-white p-2 hover:border-slate-400">
                <Bell className="h-4 w-4 text-slate-600" />
                {unread > 0 && (
                  <span className="absolute -top-1 -right-1 grid h-4 min-w-4 place-items-center rounded-full bg-[#EA580C] px-1 text-[10px] font-bold text-white">
                    {unread}
                  </span>
                )}
              </Link>
            )}
            <span className="hidden sm:grid h-9 w-9 place-items-center rounded-full bg-[#0F172A] text-xs font-bold text-white">
              {String(user.name || "U").slice(0, 2).toUpperCase()}
            </span>
            <button onClick={async () => { await logout(); navigate("/login"); }}
              data-testid="header-logout-btn" aria-label="Sign out"
              className="sm:hidden grid h-9 w-9 place-items-center rounded-full border border-slate-200 bg-white text-slate-600 hover:border-slate-400">
              <LogOut className="h-4 w-4" strokeWidth={1.8} />
            </button>
          </div>
        </header>
        <main className="flex-1 p-5 lg:p-8">
          <SessionLock>
            <Outlet />
          </SessionLock>
        </main>
        <Footer />
      </div>
    </div>
  );
}

export const NoAccess = ({ what = "this module" }) => (  <div className="panel p-10 text-center" data-testid="no-access">
    <ShieldCheck className="h-7 w-7 text-slate-300 mx-auto" strokeWidth={1.4} />
    <p className="font-ui font-semibold text-slate-900 mt-4">Access restricted</p>
    <p className="text-sm text-slate-500 mt-2">You do not have permission to view {what}. Ask your administrator to enable it.</p>
  </div>
);
